import clsx from 'clsx';
import { match } from 'ts-pattern';
import { shift, flip } from '@floating-ui/react';
import React, { useCallback, useMemo } from 'react';

import { colors } from '@ovpn-ui/styles';
import { isLatestIphoneOS } from '@ovpn-ui/utils';
import { AlertCircle, ChevronDown, DotsVertical } from '@ovpn-ui/icons';
import {
    Button,
    Tooltip,
    IconButton,
    DropdownBase,
    useDropdownBaseToggler,
    TooltipProps,
    ButtonOwnProps,
} from '@ovpn-ui/core';

import { MenuOption } from './MenuButton.styles';

const middlewares = [shift({ padding: 24 }), flip()];

const CloseHandlerEventName = isLatestIphoneOS() ? 'onTouchEnd' : 'onClick';

type MenuButtonProps = ButtonOwnProps & {
    actions: Array<{
        id: string;
        label: React.ReactNode;
        onClick: React.MouseEventHandler;
        disabled?: boolean;
        disabledDescription?: React.ReactNode;
    }>;

    Trigger?: 'button' | 'iconButton' | React.ReactElement;

    /**
     * Button label required for 'button' Trigger variant;
     */
    label?: string;

    /**
     * Passed to Trigger, unless custom trigger is provided.
     *
     * "DotsVertical" for "iconButton" by default;
     * "ChevronDown" for "button" by default;
     */
    icon?: React.ReactElement;

    className?: string;

    tooltip?: TooltipProps['children'];
};

export function MenuButton({
    Trigger,
    actions,
    className,
    label,
    icon,
    tooltip,
    ...rest
}: MenuButtonProps) {
    const { open, toggleOpen, closeDropdown, toggleWithKeyPress } = useDropdownBaseToggler();

    const optionsElement = useMemo(() => {
        const makeHandleItemClick = (actionClickHandler: any) => ({
            [CloseHandlerEventName]: () => {
                actionClickHandler?.();
                closeDropdown();
            },
        });

        const options = actions.map(action => (
            <MenuOption
                key={action.id}
                role="listitem"
                id={action.id}
                $disabled={action.disabled}
                {...(action.disabled ? {} : makeHandleItemClick(action.onClick))}
            >
                <span className="option-name">{action.label}</span>
                {action.disabled && (
                    <span className="option-description">
                        <AlertCircle size={16} strokeWidth={2} color={colors.brandOrange} />
                        <span className="content">{action.disabledDescription}</span>
                    </span>
                )}
            </MenuOption>
        ));

        return options;
    }, [actions, closeDropdown]);

    const handleChange = useCallback(
        (openState: boolean) => {
            if (!openState) {
                closeDropdown();
            }
        },
        [closeDropdown],
    );

    const commonTriggerProps: ButtonOwnProps = {
        size: 'sm',
        variant: 'ghost',
        onClick: toggleOpen,
        onKeyUp: toggleWithKeyPress,
        className: clsx(open && 'opened', 'menu-button-trigger', className),
    };

    const trig = match(Trigger)
        .with(undefined, 'button', () =>
            tooltip ? (
                <Tooltip
                    trigger={
                        <Button {...commonTriggerProps} {...rest}>
                            {label}
                            {icon ?? <ChevronDown />}
                        </Button>
                    }
                >
                    {tooltip}
                </Tooltip>
            ) : (
                <Button {...commonTriggerProps} {...rest}>
                    {label}
                    {icon ?? <ChevronDown />}
                </Button>
            ),
        )
        .with('iconButton', () => (
            <IconButton aria-label={tooltip as unknown as string} {...commonTriggerProps}>
                {icon ?? <DotsVertical />}
            </IconButton>
        ))
        .otherwise((TElement: React.ReactElement) =>
            React.cloneElement(TElement, {
                $on: open,
                onClick: toggleOpen,
                onKeyUp: toggleWithKeyPress,
                className: clsx(commonTriggerProps.className, TElement.props.className),
            }),
        );

    return (
        <DropdownBase
            open={open}
            onChange={handleChange}
            trigger={trig}
            middlewares={middlewares}
            className={`${className}-popup cloud-menu-popup`}
        >
            <div role="list">{optionsElement}</div>
        </DropdownBase>
    );
}
